<template>
  <main class="bg-black c-white">
    <section class="relative">
      <div class="relative">
        <div v-motion-zoom-out class="absolute-0" :style="`transform: translateY(-${(y) * 0.025}rem)`">
          <base-picture v-bind="header.backgroundImage" class="w-full" />
        </div>
        <div v-style:p="{ sm: '7.5rem 1rem 0', md: '8.5rem 2rem 0' }" class="z-10 mx-a">
          <div>
            <h1
              v-motion-icons-title-fade
              v-style:text-size="{ sm: '12.5vw', md: '8.75vw' }"
              class="title-1 lh-none uppercase"
            >
              {{ header.title }}
            </h1>

            <div v-motion-icons-carousel-fade class="flex">
              <vf-carousel
                v-style:m="{ sm: 'calc(13.9vw - 1.5rem) auto 13.9vw', md: 'calc(5.2vw - 1.5rem) auto 5.2vw' }"
                class="w-screen -mx-4 md:mx-a"
                class-controls="!hidden"
                @scroll-end="changeActiveItem"
              >
                <div class="md:hidden" style="width: calc((100% - 50dvw) / 2)" />
                <base-link
                  v-for="({ image, name, to }, key) in header.products"
                  :key
                  ref="carouselItems"
                  v-style:w="{ sm: '50dvw', md: '18.75rem' }"
                  class="group f-col snap-center pt-6 md:w-1/5 md:px-6"
                  :to
                >
                  <base-picture
                    :alt="name"
                    class="flex center duration-500 md:scale-100 md:group-hover:scale-120"
                    :class="[key === activeProductIndex ? 'scale-100' : 'scale-80']"
                    :src="image"
                  />
                  <h2
                    v-style:text-size="{ sm: '3.61vw', md: '0.9375vw' }"
                    class="relative mx-a mt-4 flex-col title-4 uppercase i-flex <md:op-0 <md:duration"
                    :class="{ '<md:op-100': key === activeProductIndex }"
                  >
                    {{ name }}
                    <span
                      class="absolute bottom-0 left-0 bg-white duration md:w-0 md:group-hover:w-full"
                      :class="[key === activeProductIndex ? '<md:w-full' : 'w-0']"
                      style="height: 1px"
                    />
                  </h2>
                </base-link>
                <div class="md:hidden" style="width: calc((100% - 50dvw) / 2)" />
              </vf-carousel>
            </div>
          </div>

          <h2
            v-motion-fade-up-visible
            v-style:text-size="{ sm: '12.5vw', md: '8.75vw' }"
            class="relative text-right title-1 lh-none uppercase"
          >
            {{ header.subtitle }}
          </h2>

          <base-link
            class="f-col cursor-pointer center title-5 md:hidden"
            style="padding-top: 5rem"
            to="#products-section"
          >
            <span>{{ header.explore }}</span>
            <vf-icon dir="down" name="chevron" size="lg" />
          </base-link>

          <p
            v-motion-fade-up-visible
            v-style="{
              'm': { sm: '9rem 0 0', md: '15.625vw 0 0 15.625vw' },
              'max-w': { sm: '100%', md: '59.25vw' },
              'text-size': { sm: '5.83vw', md: '3.23vw' },
            }"
            class="px-4 lh-tight"
          >
            <span class="relative mr-1 text-sm md:text-2xl" style="top: -0.25rem">{{ introYear }}</span>
            {{ introCopy }}
          </p>
        </div>
        <div class="sticky bottom-0 left-0 z-10 ml-6 w-10 <md:hidden">
          <custom-arrow-scroll
            v-motion-fade-delayed
            class="absolute bottom-0 mb-2 c-white"
            size="md"
            to="#products-section"
          />
        </div>
      </div>
    </section>
    <section id="products-section" style="margin-top: 8.5rem">
      <div
        v-for="({ id, backgroundImage, description, title, cta, silhouetteImage }, key) in mainSection"
        :key
        :id
        class="md:flex md:wrap <md:scroll-mt-9"
        :class="{ 'md:flex-row-reverse': key % 2 !== 0 }"
      >
        <div v-motion-fade-up-visible v-style:m="{ sm: '11.11vw 0', md: '0' }" class="w-full self-center text-center md:w-1/2 md:text-left" style="padding: 0 8.33vw">
          <base-picture
            v-style="{
              m: { md: '0 0 0 -2.8vw' },
              w: { sm: '44.44vw', md: '15.625vw' },
            }"
            v-bind="silhouetteImage"
            style="opacity: 0.3"
          />
          <h2 v-style="{ 'm': { sm: '-13.9vw 0 8.61vw', md: '-5.73vw 0 2.6vw' }, 'text-size': { sm: '8.61vw', md: '3.23vw' } }" class="display-2">
            {{ title }}
          </h2>
          <p v-style:text-size="{ sm: '3.61vw', md: '1.15vw' }" class="lh-tight">
            {{ description }}
          </p>
          <hr v-style:w="{ sm: '100%', md: '24.47917vw' }" class="my-10 b-grey-70 op-50">
          <div v-style:w="{ sm: '100%', md: '16.25rem' }" class="f-col gap-2">
            <vf-button
              v-for="({ label, to }, i) in cta"
              :key="i"
              class="w-full"
              :to
              variant="primary-inverse"
            >
              {{ label }}
            </vf-button>
          </div>
        </div>
        <div class="w-full md:w-1/2">
          <base-picture v-bind="backgroundImage" />
        </div>
      </div>
    </section>
  </main>
</template>

<script lang="ts" setup>
definePageMeta({ header: { transparent: true } })

const { data, error } = await useApi().internal.getStaticPage('our-icons')
if (!data.value) throw createError({ ...error.value!, fatal: true })

const { header, introYear, introCopy, mainSection } = data.value

const { y } = useScroll(window)

const carouselItems = ref<(ComponentPublicInstance | null)[]>([])
const activeProductIndex = ref(0)

const changeActiveItem = () => {
  // Workaround for activeItem due to element sizing and center snap
  carouselItems.value.forEach((item, i) => {
    const { left } = item?.$el.getBoundingClientRect()
    if (left < 250 && left > 0) activeProductIndex.value = i
  })
}

onMounted(() => {
  // Ensure that page renders at top on load to match 1.0 EMEA site
  window.history.scrollRestoration = 'manual'

  changeActiveItem()
})

onUnmounted(() => {
  window.history.scrollRestoration = 'auto'
})
</script>
